import { apiClient, ApiResponse } from '@api/apiClient';
import { V4ApiResponse } from '@api/types';
import { ToastType } from '@components/Toast/Toast';
import { ConfigManager } from '@utils/ConfigManager';
import { logError } from '@utils/errorLogging';
import { createError } from '@utils/errorUtils';
import { handleToast } from '@utils/utils';
import get from 'lodash/get';
import {
  LoadSearchPayload,
  LoadSearchRes,
  RevenueSearchData,
  RevenueSearchExportQueued,
  RevenueSearchExportStatus,
} from '@store/orders/types';

export async function fetchRevenueSearchData(
  payload: unknown,
  showToast = true,
) {
  try {
    const response: V4ApiResponse<{
      data: RevenueSearchData[];
      totalCount: number;
    }> = await apiClient.post('/reporting/getrevenuesearch', payload);

    if ('value' in response) {
      const value = get(response, 'value');
      if (showToast && value.data.length > 4000)
        handleToast(
          ToastType.Success,
          'Order Search have more than 4000 records, please refine your search or click on export button to view the full data.',
        );
      return value;
    }
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch Revenue Search Data');
    handleToast(ToastType.Error, e.message);
  }
}

export async function getLoadSearchData(payload: LoadSearchPayload) {
  try {
    const response: V4ApiResponse<LoadSearchRes> = await apiClient.post(
      '/load/searchload',
      payload,
    );
    if ('value' in response) {
      const value = get(response, 'value');
      if (value.totalCount > 4000)
        handleToast(
          ToastType.Success,
          'Load Search have more than 4000 records, please refine your search or click on export button to view the full data.',
        );
      return value;
    }
  } catch (error: unknown) {
    const e = createError(error, 'Failed to fetch Load Data');
    handleToast(ToastType.Error, e.message);
  }
}

export async function getLoadSearchOrRevenueSearchExportData(
  payload: LoadSearchPayload,
  type: 'loadSearch' | 'revenueSearch',
) {
  try {
    const apiUrl =
      type === 'loadSearch'
        ? '/load/exportsearchload'
        : '/reporting/getrevenuesearchexport';
    const t = await apiClient.post(apiUrl, payload);
    const a = document.createElement('a');
    document.body.appendChild(a);
    const url = window.URL.createObjectURL(new Blob([t as unknown as string]));
    a.href = url;
    a.download = `${
      type === 'loadSearch' ? 'LoadSearch' : 'RevenueSearch'
    }.csv`;
    a.click();
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    }, 0);
  } catch (error: unknown) {
    const e = createError(error, 'Failed to export Load Data');
    handleToast(ToastType.Error, e.message);
  }
}

export async function startRevenueSearchExport(
  payload: LoadSearchPayload,
): Promise<RevenueSearchExportQueued> {
  try {
    const response:
      | RevenueSearchExportQueued
      | ApiResponse<RevenueSearchExportQueued> = await apiClient.post(
      '/reporting/revenuesearchexports',
      payload,
    );
    if ('jobId' in response) {
      return response;
    }
    if (response.isSuccess && 'value' in response) {
      return get(response, 'value');
    }
    throw new Error(
      response.errors?.[0]?.message ?? 'Export could not be queued.',
    );
  } catch (error: unknown) {
    logError('startRevenueSearchExport', error as Error);
    throw createError(error, 'Failed to start Revenue Search export');
  }
}

export async function getRevenueSearchExportStatus(
  jobId: number,
): Promise<RevenueSearchExportStatus> {
  try {
    const response: ApiResponse<RevenueSearchExportStatus> =
      await apiClient.get(`/reporting/revenuesearchexports/${jobId}`);
    if (response.isSuccess && 'value' in response) {
      return get(response, 'value');
    }
    throw new Error(
      response.errors?.[0]?.message ?? 'Export status could not be retrieved.',
    );
  } catch (error: unknown) {
    logError('getRevenueSearchExportStatus', error as Error);
    throw createError(error, 'Failed to get Revenue Search export status');
  }
}

/**
 * Restores the current user's most recent Revenue Search export job on page load, so a
 * queued/processing/completed job is not lost if the user navigates away and back.
 */
export async function getLatestRevenueSearchExportJob(): Promise<
  RevenueSearchExportStatus | undefined
> {
  try {
    const response: ApiResponse<RevenueSearchExportStatus | null> =
      await apiClient.get('/reporting/revenuesearchexports/latest');
    if (response.isSuccess && 'value' in response) {
      return get(response, 'value') ?? undefined;
    }
    throw new Error(
      response.errors?.[0]?.message ??
        'Latest export job could not be retrieved.',
    );
  } catch (error: unknown) {
    logError('getLatestRevenueSearchExportJob', error as Error);
    throw createError(error, 'Failed to get latest Revenue Search export job');
  }
}

export function downloadRevenueSearchExportFile(downloadUrl: string) {
  window.open(`${ConfigManager.apiUrl}${downloadUrl}`, '_blank');
}
